/**
 * @lines98/client/services/book-api — typed wrapper вокруг серверной дорожки «Книги
 * реплеев» (ADR-0023 §API-контракт). ЕДИНЫЙ сервисный слой Книги: и страница `/book`
 * (тикет A), и kit-дискета (тикет B) ходят к серверу ТОЛЬКО отсюда — не свой fetch в
 * экранах (code-structure §3 «один источник правды на ручку»; AC-A16/AC-B10).
 *
 * Endpoints (все required-auth — Книга per-account; гость → 401 → клиент ведёт на логин):
 *   - GET  /book/capacity                      — server-truth счётчики (own.count; others.{used,capacity}).
 *   - GET  /book/own?cursor=&mode=             — альбом «Свои» (query-over-replays, keyset).
 *   - GET  /book/others?cursor=&mode=          — альбом «Чужие» (bookmarks, keyset).
 *   - POST /book/others/bookmark { short_id }  — заложить чужую партию (idempotency-key).
 *   - DELETE /book/others/bookmark/:short_id   — убрать закладку (освободить слот).
 *
 * Контракт совпадает с ADR-0023 (серверная дорожка пишется параллельно по тому же ADR).
 * Если сервер ответит иной формой — расхождение ловится здесь (defensive shape-guard →
 * server_error/network), не паникой в экране.
 *
 * Anti-shadow / gate (НЕ здесь): открытие партии идёт ОБЩИМ replay-flow
 * (`GET /api/replay/:short_id` → gate §3A), а не через Книгу — book-api отдаёт ТОЛЬКО
 * метаданные строк (short_id + агрегаты), без `moves[]`/`seed` (ADR-0023). Овершлоу 5/5 —
 * структурированный `409 album_full` (НЕ удаляет, НЕ паника); клиент рисует попап.
 *
 * Cross-references:
 *   - docs/decisions/ADR-0023-replay-book-data-model/README.md §API-контракт.
 *   - services/api-client.ts (Bearer + envelope), services/replay-api.ts (образец стиля).
 *   - lib/idempotency-key.ts (X-Idempotency-Key для POST bookmark).
 */

import { apiClient } from './api-client.js';

// =============================================================================
// Public types (snake_case wire — Книга не маппит в camelCase: строки отрисовываются
// форматтерами по полям as-is, маппинг был бы лишним дублем формы).
// =============================================================================

/** Альбом — server-derived (`replay.player_id == me ? own : others`). */
export type BookAlbum = 'own' | 'others';

/** Вкладка режима строки (server-truth: predict|classic|blind|daily). */
export type BookTab = 'predict' | 'classic' | 'blind' | 'daily';

/** Одна строка списка Книги (метаданные — БЕЗ moves[]/seed). */
export interface BookRow {
  readonly short_id: string;
  readonly score_verified: number;
  readonly moves_count: number;
  /** ISO-8601 UTC — когда партия сыграна (Свои) / для отображения (Чужие). */
  readonly finished_at: string;
  readonly mode: string;
  readonly is_daily: boolean;
  /** Вкладка (predict|classic|blind|daily) — server-truth (§2.4 спеки). */
  readonly tab: BookTab;
  /** ISO-8601 UTC — когда заложена (ТОЛЬКО «Чужие»). */
  readonly saved_at?: string;
  /**
   * ТОЛЬКО «Чужие»: доступна ли партия (false — осиротевшая по Q3, `replay_id IS NULL`,
   * GDPR-каскад владельца). UI рисует «Партия недоступна» + «Убрать». «Свои» — всегда true.
   */
  readonly available?: boolean;
}

/** Счётчики ёмкости (server-truth для UI). `capacity` — ТОЛЬКО у «Чужих». */
export interface BookCapacity {
  readonly own: { readonly count: number };
  readonly others: { readonly used: number; readonly capacity: number };
}

/** Результат GET /book/capacity. */
export type CapacityResult =
  | { readonly ok: true; readonly capacity: BookCapacity }
  | { readonly ok: false; readonly reason: 'unauthorized' | 'network' | 'server_error' };

/** Результат GET /book/own | /book/others (страница списка + keyset-курсор). */
export type ListResult =
  | {
      readonly ok: true;
      readonly album: BookAlbum;
      readonly items: ReadonlyArray<BookRow>;
      /** keyset-курсор следующей страницы (null — последняя). */
      readonly nextCursor: string | null;
      /**
       * ТОЛЬКО «Свои» (server-truth, owner-decision G-2 2026-06-24): true, когда гостю
       * усечён архив (≥1 вкладка-режим имеет больше игр, чем `REPLAY_BOOK_OWN_GUEST_CAP_PER_MODE`,
       * → новее-показанных скрыты до логина). Клиент рисует объясняющий upsell под списком.
       * Всегда `false` у зарегистрированного игрока (капа нет) и у «Чужих» (поле отсутствует
       * в ответе → defensive false). См. server own-list.ts `OwnAlbumPage.guest_capped`.
       */
      readonly guestCapped: boolean;
      /**
       * ТОЛЬКО «Свои» (server-truth, owner-directive 2026-06-25): true, когда игрок НЕ
       * зарегистрирован (registered=false — аноним-mint без OAuth/local). Клиент показывает
       * гостевую подпись «Свои» ВСЕГДА (даже при ≤3 играх), не только при усечении
       * `guestCapped`. На «Чужих» / у зарегистрированного отсутствует → defensive false.
       * См. server routes/book.ts `is_guest`.
       */
      readonly isGuest: boolean;
    }
  | { readonly ok: false; readonly reason: 'unauthorized' | 'network' | 'server_error' };

/**
 * Результат POST /book/others/bookmark. `album_full` — структурированный овершлоу
 * (409, НЕ паника, НЕ удаление) → клиент рисует попап «Нет места в книге».
 */
export type BookmarkResult =
  | { readonly ok: true; readonly result: 'saved' | 'already_saved' }
  | { readonly ok: false; readonly reason: 'album_full'; readonly used: number; readonly capacity: number }
  | { readonly ok: false; readonly reason: 'own_game_not_bookmarkable' }
  | { readonly ok: false; readonly reason: 'replay_not_found' }
  | { readonly ok: false; readonly reason: 'unauthorized' | 'network' | 'server_error' };

/** Результат DELETE /book/others/bookmark/:short_id. */
export type RemoveResult =
  | { readonly ok: true; readonly result: 'removed' | 'not_in_book' }
  | { readonly ok: false; readonly reason: 'unauthorized' | 'network' | 'server_error' };

// =============================================================================
// Internal helpers
// =============================================================================

/** 401 → 'unauthorized' (гость / отозванная сессия): клиент ведёт на логин. */
function authReason(status: number): 'unauthorized' | 'server_error' {
  return status === 401 ? 'unauthorized' : 'server_error';
}

function asObj(body: unknown): Record<string, unknown> {
  return typeof body === 'object' && body !== null ? (body as Record<string, unknown>) : {};
}

function asRows(value: unknown): BookRow[] {
  return Array.isArray(value) ? (value as BookRow[]) : [];
}

// =============================================================================
// Public API
// =============================================================================

/** GET /book/capacity — server-truth счётчики (own.count; others.{used,capacity}). */
export async function getBookCapacity(): Promise<CapacityResult> {
  let res;
  try {
    res = await apiClient.get<unknown>('/book/capacity');
  } catch {
    return { ok: false, reason: 'network' };
  }
  if (!res.ok) return { ok: false, reason: authReason(res.status) };
  const b = asObj(res.body);
  const own = asObj(b['own']);
  const others = asObj(b['others']);
  if (typeof own['count'] !== 'number' || typeof others['used'] !== 'number') {
    return { ok: false, reason: 'server_error' };
  }
  return {
    ok: true,
    capacity: {
      own: { count: own['count'] },
      others: {
        used: others['used'],
        capacity: typeof others['capacity'] === 'number' ? others['capacity'] : 0,
      },
    },
  };
}

/** Общий загрузчик списка альбома (own|others) с keyset-пагинацией + вкладкой режима. */
async function getBookAlbum(
  album: BookAlbum,
  opts: { cursor?: string | null; mode?: BookTab } = {},
): Promise<ListResult> {
  const params = new URLSearchParams();
  if (opts.cursor) params.set('cursor', opts.cursor);
  if (opts.mode) params.set('mode', opts.mode);
  const qs = params.toString();
  const path = `/book/${album}${qs ? `?${qs}` : ''}`;

  let res;
  try {
    res = await apiClient.get<unknown>(path);
  } catch {
    return { ok: false, reason: 'network' };
  }
  if (!res.ok) return { ok: false, reason: authReason(res.status) };
  const b = asObj(res.body);
  const nextCursor = typeof b['next_cursor'] === 'string' ? b['next_cursor'] : null;
  // guest_capped — server-truth поле ТОЛЬКО ответа /book/own (G-2). Defensive: на «Чужих»
  // его нет → false; на «Своих» у зарегистрированного — сервер шлёт false. boolean-guard
  // (как прочие поля here) ловит расхождение формы → false (не паника в экране).
  const guestCapped = b['guest_capped'] === true;
  // is_guest — server-truth «не зарегистрирован» (ТОЛЬКО /book/own). Defensive false.
  const isGuest = b['is_guest'] === true;
  return { ok: true, album, items: asRows(b['items']), nextCursor, guestCapped, isGuest };
}

/** GET /book/own — альбом «Свои» (query-over-replays, keyset). */
export function getOwnAlbum(opts?: { cursor?: string | null; mode?: BookTab }): Promise<ListResult> {
  return getBookAlbum('own', opts);
}

/** GET /book/others — альбом «Чужие» (bookmarks, keyset). */
export function getOthersAlbum(opts?: { cursor?: string | null; mode?: BookTab }): Promise<ListResult> {
  return getBookAlbum('others', opts);
}

/**
 * POST /book/others/bookmark — заложить ЧУЖУЮ партию в альбом «Чужие». `idempotencyKey`
 * (ADR-0005 Option B, минтит вызывающий kit per-действие через `lib/idempotency-key`)
 * защищает от двойного применения при сетевом ретрае. Сервер server-derives `album`;
 * своя партия → 409 own_game_not_bookmarkable (backstop — дискеты на своих нет вовсе).
 * Полный альбом → 409 album_full (овершлоу-попап, НЕ удаление).
 */
export async function saveOthersBookmark(
  shortId: string,
  idempotencyKey: string,
): Promise<BookmarkResult> {
  let res;
  try {
    res = await apiClient.post<unknown>(
      '/book/others/bookmark',
      { short_id: shortId },
      { headers: { 'X-Idempotency-Key': idempotencyKey } },
    );
  } catch {
    return { ok: false, reason: 'network' };
  }
  if (res.ok) {
    const b = asObj(res.body);
    return { ok: true, result: b['result'] === 'already_saved' ? 'already_saved' : 'saved' };
  }
  // Структурированные 409: album_full (овершлоу) / own_game_not_bookmarkable; 404 not_found.
  const b = asObj(res.body);
  const code = b['code'];
  if (res.status === 409 && code === 'album_full') {
    return {
      ok: false,
      reason: 'album_full',
      used: typeof b['used'] === 'number' ? b['used'] : 0,
      capacity: typeof b['capacity'] === 'number' ? b['capacity'] : 0,
    };
  }
  if (res.status === 409 && code === 'own_game_not_bookmarkable') {
    return { ok: false, reason: 'own_game_not_bookmarkable' };
  }
  if (res.status === 404) return { ok: false, reason: 'replay_not_found' };
  return { ok: false, reason: authReason(res.status) };
}

/**
 * DELETE /book/others/bookmark/:short_id — убрать закладку текущего игрока (освобождает
 * слот; партию НЕ трогает — прямая ссылка `/r/<short_id>` работает всегда). Идемпотентно
 * (нет строки → not_in_book). Идёт через `apiClient.del` — единый ствол транспорта
 * (Bearer / no-store / stale-identity recovery), серверный контракт = DELETE, не POST.
 */
export async function removeOthersBookmark(shortId: string): Promise<RemoveResult> {
  let res;
  try {
    res = await apiClient.del<unknown>(`/book/others/bookmark/${encodeURIComponent(shortId)}`);
  } catch {
    return { ok: false, reason: 'network' };
  }
  if (!res.ok) return { ok: false, reason: authReason(res.status) };
  const b = asObj(res.body);
  return { ok: true, result: b['result'] === 'not_in_book' ? 'not_in_book' : 'removed' };
}
